import PortalLayout from '@/components/layout/portal/PortalLayout';
import IdleModal from '@/components/modal/IdleModal';
import { useAppSelector } from '@/state/hooks';
import { Account, selectAccountType, selectCredentials, selectCurrentAccount } from '@/state/reducers/authSlice';
import { useDisclosure, UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/common/Button';
import CopyButton from '@/components/common/CopyButton';
import PortalCurrencyIcon from '@/components/common/PortalCurrencyIcon';
import { StatusPill, StatusTypes } from '@/components/common/StatusPill';
import DeactivateBankWalletModal from '@/components/modal/DeactivateBankWalletModal';
import PortalWalletDetailsModal from '@/components/modal/PortalWalletDetailsModal';
import PortalWhitelistModal from '@/components/modal/PortalWhitelistModal';
import PortalBankWalletStatusCard from '@/components/portal-modules/PortalBankWalletStatusCard';
import PortalRecentTransactionsTable from '@/components/portal-modules/PortalRecentTransactionsTable';
import { formatToDate, getOperationalCurrentTradeDate } from '@/helpers/dateHelper';
import { selectDimissedWhitelist } from '@/state/reducers/portalPlatformSlice';
import { selectTablePagination } from '@/state/reducers/tableSettingSlice';
import { FlattenPortalRequest, PortalRequestResponse } from '@/types/sso';
import Unauthorized401 from '@/Unauthorized401';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalAuthentication from '@/utils/hooks/usePortalAuthentication';
import { fetchPortalAPI, usePortalSWR, useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { subYears } from 'date-fns';
import {
    MdCheck,
    MdContentCopy,
    MdDeleteOutline,
    MdOutlineAccountBalanceWallet,
    MdOutlineVisibility,
    MdRefresh
} from 'react-icons/md';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router-dom';

const cardHeight = 99.1;
const currentTradeDate = getOperationalCurrentTradeDate();

function PortalWallets() {
    usePortalAuthentication();

    const { jwt, walletInfoApi, foundEntity, hasPermission, onFetchJWT } = useSso();

    const navigate = useNavigate();
    const accountType = useAppSelector(selectAccountType);
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const pagination = useAppSelector(selectTablePagination);
    const dismissedWhitelistRequests = useAppSelector(selectDimissedWhitelist);

    const lang = useLanguage();
    const idleDisclosure = useDisclosure(false);
    const walletInsertDisclosure = useDisclosure(false);
    const walletDetailsDisclosure = useDisclosure(false);
    const deactivateWalletDisclosure = useDisclosure(false);

    const [offset, setOffset] = useState<number>(0);
    const [selectedWallet, setSelectedWallet] = useState<FlattenPortalRequest>();

    const walletRequestPayload = useMemo(
        () => ({
            startDate: formatToDate(subYears(currentTradeDate, 1)),
            endDate: formatToDate(currentTradeDate),
            entityId: foundEntity?.entityId.toString(),
            limit: 999,
            offset: offset.toString(),
            requestType: 'WHITELIST_WALLET,REMOVE_WALLET'
        }),
        [foundEntity?.entityId, pagination.pageSize, offset]
    );

    const walletWhitelistApi = usePortalSWR<PortalRequestResponse>('/api/requests/list-whitelist-wallets', () =>
        fetchPortalAPI(onFetchJWT, '/api/requests/list', jwt, walletRequestPayload, 'POST')
    );

    const { data: trueWalletsData = [] } = walletInfoApi;
    const { data: requestWalletsData = { data: [], total: 0 } } = walletWhitelistApi;

    const isVloading = useMemo(() => {
        return (
            walletInfoApi.isLoading ||
            walletInfoApi.isValidating ||
            walletWhitelistApi.isLoading ||
            walletWhitelistApi.isValidating
        );
    }, [walletInfoApi, walletWhitelistApi]);

    const walletsData = useMemo(() => {
        if (trueWalletsData && requestWalletsData) {
            const combinedWalletsData = [
                ...convertTrueWalletsData(trueWalletsData),
                ...convertRequestWalletsData(
                    requestWalletsData.data.filter(
                        (r) =>
                            ['INITIATED', 'CANCELED', 'REJECTED'].includes(r.requestStatus) &&
                            !dismissedWhitelistRequests.wallets.includes(r.id)
                    )
                )
            ];

            const walletsToBeRemoved = requestWalletsData.data
                .filter((r) => r.requestType === 'REMOVE_WALLET')
                .map((r) => r.details.walletId);

            combinedWalletsData.sort((a, b) => {
                if (a.details.active && b.details.active) return -1;
                if (b.details.active && a.details.active) return 1;
                return 0;
            });

            return combinedWalletsData.filter(
                (item) =>
                    item.details?.active &&
                    !(walletsToBeRemoved.includes(item.walletId) || walletsToBeRemoved.includes(item.details.walletId))
            );
        }
        return [];
    }, [trueWalletsData, requestWalletsData, dismissedWhitelistRequests]);

    const isDisabled = useMemo(() => {
        const account = credentials?.accounts.find((acc) => acc.code === currentAccount) as Account;
        const accountProperty = account?.properties.find((property) => property.key === 'TYPE');
        return accountProperty?.value !== 'NOP';
    }, [currentAccount]);

    const requestPayload = useMemo(
        () => ({
            startDate: formatToDate(subYears(currentTradeDate, 1)),
            endDate: formatToDate(currentTradeDate),
            entityId: foundEntity?.entityId.toString(),
            limit: 999,
            offset: offset.toString(),
            requestType: 'DEPOSIT,WITHDRAWAL,INTERNAL_TRANSFER',
            requestStatus: undefined
        }),
        [foundEntity?.entityId, pagination.pageSize, offset]
    );

    const recentTransactionsApi = usePortalSWR<PortalRequestResponse>('/api/requests/list-recent-transactions', () =>
        fetchPortalAPI(onFetchJWT, '/api/requests/list', jwt, requestPayload, 'POST')
    );

    const { data: recentTransactionsData } = recentTransactionsApi;

    useEffect(() => {
        if (accountType === 'MARGIN') {
            navigate('/portal');
        } else {
            walletInfoApi.mutate();
            walletWhitelistApi.mutate();
            recentTransactionsApi.mutate();
        }
    }, [currentAccount, accountType]);

    const isAuthorized = hasPermission('REQUEST_WHITELIST');

    return credentials ? (
        <PortalLayout isAuthorized={isAuthorized}>
            {isAuthorized && (
                <Fragment>
                    <div className="flex flex-col w-full gap-2 sm:gap-4 p-2 sm:p-6 text-brand-neutral-200 mt-16 lg:mt-0">
                        <div className="flex flex-wrap md:h-[34px] gap-2 w-full justify-between">
                            <div className="font-bold text-xl">{lang.navigation.wallets}</div>
                            <Button
                                type="submit"
                                className="h-fit w-fit !gap-2 text-xs"
                                onClick={walletInsertDisclosure[1].open}
                                disabled={isDisabled}>
                                <MdOutlineAccountBalanceWallet className="w-4 h-4" />
                                {lang.portal.addWalletAddress}
                            </Button>
                        </div>

                        <div className="flex flex-col gap-4 bg-brand-background-dark border border-brand-neutral-700 text-brand-neutral-200 rounded-md">
                            <div className="flex items-center justify-between w-full p-4 sm:p-6 pt-4 !pb-0">
                                <div className="">{lang.portal.whitelistedBankAccounts}</div>
                                <div className="flex items-center gap-2 justify-end">
                                    <div
                                        className={cn(
                                            'border border-brand-neutral-700 rounded-md p-1.5 cursor-pointer',
                                            { 'hover:bg-brand-primary': !isVloading }
                                        )}
                                        onClick={() => {
                                            if (!isVloading) {
                                                walletInfoApi.mutate();
                                                walletWhitelistApi.mutate();
                                            }
                                        }}>
                                        <MdRefresh
                                            className={cn('w-4 h-4', {
                                                'animate-spin text-brand-neutral-400': isVloading
                                            })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap w-full gap-2 sm:gap-4 max-h-[380px] overflow-y-auto p-4 sm:p-6 pb-4 !pt-0">
                                {isVloading ? (
                                    <div className="flex flex-col w-full gap-2 sm:gap-4">
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <Skeleton containerClassName="flex-1" height={cardHeight} />
                                        <div className="flex gap-2">
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                            <Skeleton className="!rounded-full" height={12} width={12} />
                                        </div>
                                    </div>
                                ) : (
                                    <Fragment>
                                        {walletsData && walletsData?.length > 0 ? (
                                            <Fragment>
                                                {walletsData.map((wallet, index) => {
                                                    const flattenWalletDetails = {
                                                        ...wallet,
                                                        ...wallet.details
                                                    };

                                                    return (
                                                        <WalletCard
                                                            key={index}
                                                            walletDetails={flattenWalletDetails}
                                                            walletDetailsDisclosure={walletDetailsDisclosure}
                                                            deactivateWalletDisclosure={deactivateWalletDisclosure}
                                                            onClick={() => setSelectedWallet(flattenWalletDetails)}
                                                        />
                                                    );
                                                })}
                                            </Fragment>
                                        ) : (
                                            <div className="text-xs text-neutral-200">
                                                {lang.portal.noWalletAddressesWhitelistedYet}
                                            </div>
                                        )}
                                    </Fragment>
                                )}
                            </div>
                        </div>
                        {/* {removedWalletsData && removedWalletsData.length > 0 && (
                            
                        <div className="flex flex-col gap-4 bg-brand-background-dark p-6 py-4 border border-brand-neutral-700 text-brand-neutral-200 rounded-md">
                        <div className="">Whitelisted Wallet Status</div>
                            
                        </div>
                        )} */}

                        <PortalBankWalletStatusCard
                            type="Wallet"
                            requestData={
                                requestWalletsData ?? {
                                    data: [],
                                    total: 0
                                }
                            }
                        />

                        <PortalRecentTransactionsTable
                            mode="crypto"
                            data={recentTransactionsData}
                            filters={undefined}
                            setFilters={() => {}}
                            defaultFilters={undefined}
                            setOffset={setOffset}
                            isLoading={recentTransactionsApi.isLoading || recentTransactionsApi.isValidating}
                            mutate={recentTransactionsApi.mutate}
                        />
                    </div>
                    <IdleModal opened={idleDisclosure[0]} handlers={idleDisclosure[1]} />
                    <PortalWhitelistModal
                        mode="wallet"
                        opened={walletInsertDisclosure[0]}
                        handlers={walletInsertDisclosure[1]}
                        onRefresh={() => {
                            walletInfoApi.mutate();
                            walletWhitelistApi.mutate();
                        }}
                    />
                    <PortalWalletDetailsModal
                        handlers={walletDetailsDisclosure[1]}
                        opened={walletDetailsDisclosure[0]}
                        onClose={walletDetailsDisclosure[1].close}
                        selectedWallet={selectedWallet}
                    />
                    <DeactivateBankWalletModal
                        mode="wallet"
                        opened={deactivateWalletDisclosure[0]}
                        handlers={deactivateWalletDisclosure[1]}
                        selectedWallet={selectedWallet}
                        onRefresh={() => {
                            walletInfoApi.mutate();
                            walletWhitelistApi.mutate();
                        }}
                    />
                </Fragment>
            )}
            {!isAuthorized && <Unauthorized401 />}
        </PortalLayout>
    ) : null;
}

export default PortalWallets;

export interface WalletCardProps {
    walletDetails: FlattenPortalRequest;
    walletDetailsDisclosure: UseDisclosureReturn;
    deactivateWalletDisclosure: UseDisclosureReturn;
    onClick: () => void;
}

export const WalletCard = (props: WalletCardProps) => {
    const { walletDetails, walletDetailsDisclosure, deactivateWalletDisclosure, onClick } = props;

    const { blockchainsApi, currenciesApi } = useSso();

    const status = useMemo(() => walletDetails?.requestStatus?.toLowerCase() as StatusTypes, [walletDetails]);

    const statusText = useMemo(() => {
        if (status === 'initiated') {
            if (walletDetails.requestType === 'REMOVE_WALLET') return 'pending removal';
            else if (walletDetails.requestType === 'WHITELIST_WALLET') return 'pending approval';
        }
        if (status === 'update') return 'pending update';
        if (status === undefined) return 'approved';
        return status;
    }, [status]);

    const networkName = useMemo(() => {
        if (walletDetails.networkName) return walletDetails.networkName;
        else if (walletDetails.networkId) {
            const blockchain = blockchainsApi.data?.find((b) => b.id === walletDetails.networkId);
            return blockchain ? blockchain.name : '';
        }
        return '';
    }, [walletDetails.networkName, blockchainsApi]);

    const assets = useMemo(() => {
        if (walletDetails.supportedAssets && walletDetails.supportedAssets.length > 0) {
            return walletDetails.supportedAssets.map((asset) => {
                const { show } = getCurrency(asset?.digitalAssetName || asset?.currencyName || '');
                return show;
            });
        } else if (walletDetails.assetIds && walletDetails.assetIds.length > 0) {
            return walletDetails.assetIds.map((assetId) => {
                const currency = currenciesApi?.data?.find((c) => c.id === assetId);
                const { show } = getCurrency(currency?.digitalAssetName || currency?.name || '');
                return show;
            });
        }
        return [];
    }, [walletDetails.supportedAssets, currenciesApi]);

    const deleteTitle = useMemo(() => {
        if (status === 'initiated') return 'Cancel Request';
        if (status === 'update') return 'Cancel Update Request';
        if (status === 'rejected') return 'Dismiss Wallet';
        return 'Deactivate Wallet';
    }, [status]);

    return (
        <div className="bg-brand-background-dark p-4 border border-brand-neutral-700 hover:border-brand-neutral-600 rounded-md w-full">
            <div className="flex gap-4">
                <div className={cn('flex flex-col gap-2 text-sm text-brand-neutral-200')}>
                    <div className={cn('flex flex-wrap gap-2 text-sm')}>
                        {walletDetails.friendlyName}
                        <StatusPill statusText={statusText} statusType={status === undefined ? 'approved' : status} />
                    </div>
                    <div className="text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                        {networkName} {walletDetails.walletAddress}
                    </div>
                    <div className="flex items-center gap-1.5 text-brand-neutral-400 text-2xs sm:text-xs leading-normal">
                        {assets.length > 0 ? (
                            assets.map((asset, index) => (
                                <div key={index} className="flex items-center gap-1">
                                    <PortalCurrencyIcon show={asset} className="!w-3 !h-3" />
                                    <span>
                                        {asset}
                                        {/* {index < assets.length - 1 && ', '} */}
                                    </span>
                                </div>
                            ))
                        ) : (
                            <span className="text-brand-neutral-400">No supported assets</span>
                        )}
                    </div>
                </div>
                <div className="flex-1 justify-end flex items-center gap-2">
                    <div className="flex items-center justify-end gap-2 min-w-12">
                        <CopyButton value={walletDetails.walletAddress}>
                            {({ copied, copy }) => (
                                <button
                                    onClick={copy}
                                    title="Copy wallet address"
                                    className="rounded-sm p-1 pr-0 items-center group">
                                    {copied ? (
                                        <MdCheck className="w-4 h-4 text-neutral-200 group-hover:text-brand-primary-light" />
                                    ) : (
                                        <MdContentCopy className="w-4 h-4 text-neutral-200  group-hover:text-brand-primary-light" />
                                    )}
                                </button>
                            )}
                        </CopyButton>
                        <button
                            title={deleteTitle}
                            onClick={() => {
                                onClick();
                                deactivateWalletDisclosure[1].open();
                            }}
                            className="text-brand-red hover:text-brand-red-light">
                            <MdDeleteOutline className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => {
                                onClick();
                                walletDetailsDisclosure[1].open();
                            }}
                            className="text-brand-neutral-200 hover:text-brand-primary-light">
                            <MdOutlineVisibility className="w-5 h-5" />
                        </button>
                        {/* <button onClick={onClick} className="text-brand-neutral-200 hover:text-brand-primary-light">
                            <MdOutlineOpenInNew className="w-5 h-5" />
                        </button> */}
                    </div>
                </div>
            </div>
        </div>
    );
};

const convertTrueWalletsData = (data: any) => {
    if (data) {
        return data.map((item: any) => ({
            ...item,
            details: {
                ...item,
                active: item.active || false
            }
        }));
    }
    return [];
};

const convertRequestWalletsData = (data: any) => {
    if (data) {
        return data.map((item: any) => ({
            ...item,
            details: {
                ...item.details,
                active: true
            }
        }));
    }
    return [];
};
